import React from 'react';
import { useTranslation } from 'react-i18next';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Hammer, ExternalLink } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { cn } from '@/lib/utils';
import { formatUsd } from '@/lib/paymentsData';

export const PaymentsByJobCard = ({ rows = [], onPick }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const top = rows.filter((r) => r.totalInvoiced > 0).slice(0, 12);

  return (
    <Card
      className="border-[hsl(var(--hairline))] bg-[hsl(var(--card))]"
      data-testid="payments-by-job"
    >
      <CardContent className="p-4">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <Hammer size={14} className="text-[hsl(var(--primary))]" />
            <h2 className="text-[10px] uppercase tracking-[0.22em] text-muted-foreground">
              {t('payments.byJob.title')}
            </h2>
          </div>
          <span className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground">
            {t('payments.byJob.count', { shown: top.length, total: rows.length })}
          </span>
        </div>
        {top.length === 0 ? (
          <div className="text-sm text-muted-foreground py-6 text-center">
            {t('payments.byJob.empty')}
          </div>
        ) : (
          <div className="space-y-2">
            {top.map((r) => {
              const pct = r.collectedPct;
              const tone =
                pct >= 90
                  ? 'success'
                  : pct >= 50
                  ? 'default'
                  : pct >= 25
                  ? 'warning'
                  : 'danger';
              const dotColor = {
                success: 'bg-[hsl(var(--success))]',
                default: 'bg-[hsl(var(--primary))]',
                warning: 'bg-[hsl(var(--warning))]',
                danger: 'bg-[hsl(var(--destructive))]',
              }[tone];
              return (
                <div
                  key={r.job_id}
                  role="button"
                  tabIndex={0}
                  onClick={() => onPick?.(r)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      onPick?.(r);
                    }
                  }}
                  data-testid={`payments-job-row-${r.job_id}`}
                  className="w-full text-left rounded-md border border-border bg-[hsl(var(--muted))]/40 px-3 py-2.5 hover:border-[hsl(var(--primary))]/30 transition-colors cursor-pointer focus:outline-none focus-visible:ring-2 focus-visible:ring-[hsl(var(--ring))]/40"
                >
                  <div className="flex items-center justify-between gap-3">
                    <div className="min-w-0">
                      <div className="text-sm font-semibold text-foreground truncate">
                        {r.job_name}
                      </div>
                      <div className="text-[11px] text-muted-foreground truncate">
                        {r.client || t('payments.byJob.noClient')} {'\u00b7'}{' '}
                        {t('payments.byJob.invoices', { count: r.invoiceCount })}
                      </div>
                    </div>
                    <div className="flex items-center gap-3 shrink-0">
                      <div className="text-right">
                        <div className="text-sm font-semibold text-foreground tabular-nums">
                          {formatUsd(r.collected)}
                        </div>
                        <div className="text-[11px] text-muted-foreground">
                          {t('payments.byJob.of', { amount: formatUsd(r.totalInvoiced) })}
                        </div>
                      </div>
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          navigate(
                            `/dashboard/active-jobs?jobId=${encodeURIComponent(r.job_id)}`
                          );
                        }}
                        className="h-7 w-7 rounded-md flex items-center justify-center text-muted-foreground hover:text-[hsl(var(--primary))] hover:bg-[hsl(var(--primary))]/10"
                        aria-label={t('payments.byJob.openJobAria')}
                        data-testid={`payments-job-open-${r.job_id}`}
                      >
                        <ExternalLink size={12} />
                      </button>
                    </div>
                  </div>
                  <div className="mt-2 flex items-center gap-2">
                    <span aria-hidden className={cn('h-1.5 w-1.5 rounded-full', dotColor)} />
                    <Progress
                      value={pct}
                      className="flex-1 h-1.5 bg-[hsl(var(--muted))]"
                      data-testid={`payments-job-progress-${r.job_id}`}
                    />
                    <span className="text-[11px] text-muted-foreground tabular-nums shrink-0">
                      {pct}%
                    </span>
                    {r.overdue > 0 ? (
                      <Badge
                        variant="outline"
                        className="uppercase text-[9px] tracking-[0.16em] border-[hsl(var(--destructive))]/30 text-[hsl(var(--destructive))] bg-[hsl(var(--destructive))]/10"
                      >
                        {t('payments.byJob.overdueBadge', { amount: formatUsd(r.overdue) })}
                      </Badge>
                    ) : null}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default PaymentsByJobCard;
